import type { Schemas } from '@kanos/api-client';
import { Button, StatusBadge } from '@kanos/ui';
import { api, Card, failure, formatDateTime, ProblemAlert, QueryContent } from '@kanos/web-core';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type PlatformTask = Schemas['PlatformTask'];

const tasksKey = ['platform', 'tasks'] as const;

const tasksQuery = queryOptions({
  queryKey: tasksKey,
  queryFn: async (): Promise<PlatformTask[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/tasks');
    if (!data) throw failure('listPlatformTasks', error, response.status);
    return data;
  },
});

function useDone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { data, error, response } = await api.POST('/api/v1/platform/tasks/{taskId}/done', {
        params: { path: { taskId } },
      });
      if (!data) throw failure('completePlatformTask', error, response.status);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tasksKey }),
  });
}

const titleOf: Record<PlatformTask['kind'], string> = {
  'remove-firm-member': 'Gelöschtes Teammitglied aus Keycloak entfernen',
  'delete-portal-account': 'Portal-Konto eines gelöschten Mandanten löschen',
  'remove-firm-organization': 'Organisation einer gelöschten Kanzlei entfernen',
  'destroy-firm-key': 'Schlüssel einer gelöschten Kanzlei vernichten',
  'delete-api-client': 'API-Client einer gelöschten Kanzlei löschen',
  'review-firm-return': 'Kanzlei bucht wieder – Rückkehr prüfen',
  'review-membership-check': 'Mitgliedschaftsprüfung angehalten – Organisation prüfen',
};

const id = (value: string | null) => <code className="break-all">{value}</code>;

/** What to do, in Keycloak or OpenBao - by ids only. */
function TaskSteps({ task }: { task: PlatformTask }) {
  switch (task.kind) {
    case 'delete-portal-account':
      return task.inUse ? (
        <p className="rounded-sm bg-warning-bg px-3 py-2 text-[0.9375rem] text-warning-fg">
          Das Konto {id(task.subject)} wurde inzwischen wieder eingeladen und gehört zu einer
          Aufnahme – nicht löschen, nur als erledigt markieren.
        </p>
      ) : (
        <p className="text-[0.9375rem]">
          Im Realm „mandant“ das Konto {id(task.subject)} löschen. Es gehört zu keiner Aufnahme
          mehr, bei keiner Kanzlei; seine Sitzungen sind beendet.
        </p>
      );
    case 'remove-firm-organization':
      return (
        <ol className="grid list-decimal gap-1 pl-5 text-[0.9375rem]">
          <li>Im Realm „kanzlei“ die Organisation {id(task.organizationId)} löschen.</li>
          <li>Konten, die danach keiner Organisation mehr angehören: löschen.</li>
        </ol>
      );
    case 'destroy-firm-key':
      return (
        <ol className="grid list-decimal gap-1 pl-5 text-[0.9375rem]">
          <li>
            In OpenBao beim Transit-Schlüssel {id(`kanos-tenant-${task.subject}`)} das Löschen
            erlauben (<code>deletion_allowed</code>).
          </li>
          <li>
            Den Schlüssel löschen – damit ist alles Versiegelte der Kanzlei, auch in Backups,
            unlesbar.
          </li>
        </ol>
      );
    case 'delete-api-client':
      return (
        <p className="text-[0.9375rem]">
          Im Realm „api“ den Client {id(task.subject)} löschen. Kanos nimmt mit ihm schon nichts
          mehr an; seine Kanzlei ist gelöscht.
        </p>
      );
    case 'review-firm-return':
      return (
        <ol className="grid list-decimal gap-1 pl-5 text-[0.9375rem]">
          <li>
            Das Abo der Kanzlei {id(task.subject)} läuft wieder. Ihr Vertragsende nimmt Kanos nicht
            selbst zurück: Sie ist gesperrt, oder das Ende hat das Kanos-Team gesetzt.
          </li>
          <li>
            Unter Plattform › Kanzleien das Vertragsende prüfen und, wenn richtig, zurücknehmen.
          </li>
        </ol>
      );
    case 'review-membership-check':
      return (
        <ol className="grid list-decimal gap-1 pl-5 text-[0.9375rem]">
          <li>
            Laut Keycloak hätte mehr als die Hälfte des Teams der Kanzlei {id(task.subject)} ihre
            Organisation auf einmal verlassen. Kanos hat nichts entzogen.
          </li>
          <li>
            Im Realm „kanzlei“ die Organisation der Kanzlei und ihre Mitglieder prüfen; ist die
            Organisation richtig, die Kanzlei fragen, ob die Personen gehen sollten.
          </li>
          <li>Die nächste tägliche Prüfung liest erneut; diese Aufgabe danach erledigen.</li>
        </ol>
      );
    case 'remove-firm-member':
      return (
        <ol className="grid list-decimal gap-1 pl-5 text-[0.9375rem]">
          <li>
            Im Realm „kanzlei“ das Konto {id(task.subject)} aus der Organisation{' '}
            {id(task.organizationId)} entfernen.
          </li>
          <li>Gehört das Konto keiner weiteren Organisation an: das Konto löschen.</li>
        </ol>
      );
  }
}

function TaskItem({ task }: { task: PlatformTask }) {
  const done = useDone();
  return (
    <li className="glass grid gap-2 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{titleOf[task.kind]}</span>
        <StatusBadge tone={task.doneAt ? 'success' : 'warning'}>
          {task.doneAt ? 'Erledigt' : 'Offen'}
        </StatusBadge>
      </div>
      <TaskSteps task={task} />
      <p className="text-sm text-muted">
        Angelegt {formatDateTime(task.createdAt)}
        {task.doneAt ? ` · erledigt ${formatDateTime(task.doneAt)}` : ''}
      </p>
      {!task.doneAt && (
        <Button
          className="justify-self-start"
          isPending={done.isPending}
          onPress={() => done.mutate(task.id)}
        >
          Als erledigt markieren
        </Button>
      )}
      {done.isError && <ProblemAlert error={done.error} />}
    </li>
  );
}

/**
 * Work for the Kanos team in Keycloak (ADR-024 § 6): Kanos holds no admin rights there, so a
 * firm's erased member is removed from its organization - and the account deleted once it
 * belongs to no firm - by an administrator of the platform; so is an erased client's portal
 * account once it answers for no intake (ADR-063). Named by ids only.
 */
export function TasksPage() {
  const tasks = useQuery(tasksQuery);
  return (
    <Card
      id="platform-tasks"
      title="Aufgaben in Keycloak"
      description="Hat eine Kanzlei ein Teammitglied gelöscht, ist es dort schon gesperrt und ohne Personendaten; ein gelöschter Mandant hat keine Aufnahme mehr, seine Portal-Sitzungen sind beendet. Ist eine Kanzlei gelöscht, bleiben ihre Organisation in Keycloak und ihr Schlüssel in OpenBao. Mitgliedschaft und Konto in Keycloak entfernt das Kanos-Team – danach hier als erledigt markieren (mit zweitem Faktor)."
    >
      <QueryContent query={tasks}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Keine Aufgaben.</p>
          ) : (
            <ul className="grid gap-2">
              {list.map((task) => (
                <TaskItem key={task.id} task={task} />
              ))}
            </ul>
          )
        }
      </QueryContent>
    </Card>
  );
}
